import {useCallback, useRef, useState} from "react";
import {fetchPendingPurchases, fetchPurchaseOrNull, isQrDraft} from "./qrDraftActions";
import {DRAFT_OUTCOMES} from "./QrScanPurchaseActions";

const MAX_LOOKUPS_PER_REFRESH = 25;

/**
 * Works out, for each scan event with a purchaseId, whether that purchase is still a pending QR draft
 * ("pending"), a pending purchase not created by QR ("pendingOther", e.g. a manual step 1 flagged
 * WEIGHT_NOT_DROPPED),
 * has been completed, or no longer exists (discarded). Returns { states: {purchaseId: state}, resolve(events) }.
 * Completed / missing are final, so they are cached and looked up only once.
 */
export function useQrPurchaseStates() {
    const finalStates = useRef(new Map()); // purchaseId -> "completed" | "missing"
    const [states, setStates] = useState({});

    const resolve = useCallback(async (events) => {
        let pending = null;
        try {
            pending = await fetchPendingPurchases();
        } catch (e) {
            pending = null; // unknown: fall back to the event outcome
        }

        const next = {};
        const toLookUp = [];
        (events || []).forEach(event => {
            const id = event.purchaseId;
            if (!id || next[id]) return;
            if (!DRAFT_OUTCOMES.includes(event.outcome)) {
                next[id] = finalStates.current.get(id) || "completed";
                return;
            }
            if (pending === null) {
                next[id] = finalStates.current.get(id) || "pending";
                return;
            }
            if (pending.has(id)) {
                next[id] = isQrDraft(pending.get(id)) ? "pending" : "pendingOther";
                return;
            }
            const known = finalStates.current.get(id);
            if (known) {
                next[id] = known;
            } else if (!toLookUp.includes(id)) {
                toLookUp.push(id);
            }
        });

        await Promise.all(toLookUp.slice(0, MAX_LOOKUPS_PER_REFRESH).map(async id => {
            let purchase;
            try {
                purchase = await fetchPurchaseOrNull(id);
            } catch (e) {
                return; // could not check right now; retried on the next refresh
            }
            if (!purchase) {
                finalStates.current.set(id, "missing");
                next[id] = "missing";
            } else if (purchase.isPartialRecord === true) {
                // not in the pending page we fetched; do not cache
                next[id] = isQrDraft(purchase) ? "pending" : "pendingOther";
            } else {
                finalStates.current.set(id, "completed");
                next[id] = "completed";
            }
        }));

        setStates(next);
    }, []);

    // Mark a purchase as gone straight away (after a discard), before the next refresh.
    const markMissing = useCallback((id) => {
        finalStates.current.set(id, "missing");
        setStates(prev => ({...prev, [id]: "missing"}));
    }, []);

    return {states, resolve, markMissing};
}
